import * as React from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";

type UserLite = {
  id: string;
  firstName: string;
  lastName: string;
  status?: string; // si dispo
};

export function ClientPicker({
  users,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  users: UserLite[];
  value?: string;
  onChange: (val: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation("adminui3");
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();
  const selected = users.find(u => u.id === value);

  const triggerLabel = selected
    ? `${selected.firstName} ${selected.lastName}` + (selected.status ? ` (${selected.status})` : "")
    : (placeholder ?? t("clientPicker.defaultPlaceholder"));

  // Liste partagée entre les deux conteneurs (Popover desktop / Sheet mobile).
  const commandBlock = (
    <Command shouldFilter className={isMobile ? "flex-1 min-h-0" : undefined}>
      <CommandInput placeholder={t("clientPicker.searchPlaceholder")} autoFocus={isMobile} />
      {/* Sur mobile la liste occupe le sheet ; sur desktop on garde la
          hauteur max par défaut du Popover. */}
      <CommandList
        className={isMobile ? "flex-1 max-h-none overflow-y-auto pb-[env(safe-area-inset-bottom)]" : undefined}
      >
        <CommandEmpty>{t("clientPicker.noResult")}</CommandEmpty>
        <CommandGroup>
          {users.map((u) => (
            <CommandItem
              key={u.id}
              value={`${u.firstName} ${u.lastName}`}
              onSelect={() => {
                onChange(u.id);
                setOpen(false);
              }}
              className={isMobile ? "py-3 text-base" : undefined}
            >
              <Check className={cn("mr-2 h-4 w-4", value === u.id ? "opacity-100" : "opacity-0")} />
              <span className="truncate">
                {u.firstName} {u.lastName}
                {u.status ? ` (${u.status})` : ""}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );

  if (isMobile) {
    // Bottom sheet plein écran : le Popover+Command est trop étroit au doigt,
    // et le picker vit parfois DANS le sheet du PaymentDialog (z-[10001]) →
    // contenu à z-[10010] et overlay à z-[10009] pour passer au-dessus.
    return (
      <>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {triggerLabel}
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-60" />
        </Button>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="bottom"
            className="z-[10010] h-[85dvh] rounded-t-3xl p-0 flex flex-col"
            overlayClassName="z-[10009]"
          >
            <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-muted" aria-hidden />
            <SheetHeader className="px-4 pt-2 pb-1 text-left">
              <SheetTitle className="text-base">
                {placeholder ?? t("clientPicker.defaultPlaceholder")}
              </SheetTitle>
            </SheetHeader>
            {commandBlock}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          {triggerLabel}
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]">
        {commandBlock}
      </PopoverContent>
    </Popover>
  );
}
